import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import TagManager from 'react-gtm-module';
import weloopai from '@weloopai/sdk';

import { MODE } from '../constants/env';
import { initializeMedallia, setGlobalValuesForMedallia } from '../medallia';
import { selectLanguage } from '../store/selectors/i18n.selectors';
import { selectSiteCountryCode, selectStoreId, selectUserJobname, selectUserUID } from '../store/selectors/userInfo';
import { selectConfiguration, selectHasFrontMonitoring } from '../store/selectors/configuration';
import { WELOOP_COUNTRY_ID, WELOOP_PROJECT_ID } from '../constants/weloop';
import { setIsMobile, detectIsMobileWidth } from '../store/appSlice';
import { selectIcareKo, selectTitle } from '../store/selectors/app';
import { useAppDispatch } from '../store/store';

const IS_PLAYWRIGHT = Boolean(globalThis.parent.Playwright);

export const SideEffects = () => {
  const title = useSelector(selectTitle);
  const lang = useSelector(selectLanguage);
  const config = useSelector(selectConfiguration);
  const hasFrontMonitoring = useSelector(selectHasFrontMonitoring);
  const userJobname = useSelector(selectUserJobname);
  const userUniqueId = useSelector(selectUserUID);
  const languageCode = useSelector(selectLanguage);
  const GTMTrackingID = config?.GTMTrackingID;
  const selectedStoreId = useSelector(selectStoreId);
  const countryCode = useSelector(selectSiteCountryCode);
  const icareKo = useSelector(selectIcareKo);
  const dispatch = useAppDispatch();

  const initializeGTM = (GTMTrackingIDParam: string) => {
    const tagManagerArgs = {
      gtmId: GTMTrackingIDParam,
      dataLayerName: 'dataLayer',
    };

    TagManager.initialize(tagManagerArgs);
  };

  useEffect(() => {
    if (!IS_PLAYWRIGHT && GTMTrackingID) {
      initializeGTM(GTMTrackingID);
    }
  }, [GTMTrackingID]);

  useEffect(() => {
    if (!IS_PLAYWRIGHT && config.hasMedallia && userJobname && languageCode && countryCode) {
      initializeMedallia();
      setGlobalValuesForMedallia({
        jobName: userJobname,
        uid: userUniqueId,
        languageCode,
        userCountry: countryCode,
        storeId: selectedStoreId.toString(),
        env: MODE,
      });
    }
  }, [config.hasMedallia, userJobname && languageCode]);

  useEffect(() => {
    if (!IS_PLAYWRIGHT && (countryCode || icareKo)) {
      const weloopLanguage = countryCode || new Intl.Locale(navigator.language).region;
      weloopai.init({
        key: WELOOP_PROJECT_ID,
        user: {
          groupId: WELOOP_COUNTRY_ID[weloopLanguage || 'EN'],
        },
      });

      return () => {
        weloopai.destroy();
      };
    }

    return undefined;
  }, [countryCode, icareKo]);

  useEffect(() => {
    const handleWindowSizeChange = () => {
      dispatch(setIsMobile(detectIsMobileWidth()));
    };

    globalThis.addEventListener('resize', handleWindowSizeChange);
    return () => globalThis.removeEventListener('resize', handleWindowSizeChange);
  }, [dispatch]);

  useEffect(() => {
    document.title = title ? `Icare - ${title}` : 'Icare';
  }, [title]);

  useEffect(() => {
    lang && document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  useEffect(() => {
    if (hasFrontMonitoring) {
      import('../utils/instrumentation/instrumentationDatadog').then(({ initMonitoring }) => initMonitoring());
    }
  }, [hasFrontMonitoring]);

  useEffect(() => {
    if (!IS_PLAYWRIGHT && hasFrontMonitoring && selectedStoreId) {
      import('@datadog/browser-rum').then(({ datadogRum }) => {
        datadogRum.setUserProperty('storeId', selectedStoreId);
      });
    }
  }, [hasFrontMonitoring, selectedStoreId]);

  return null;
};
